import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import api from '../api/clients';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

export default function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated, setLoading, user } = useAuthStore();
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ usuario: false, password: false });

  useEffect(() => {
    if (isAuthenticated && user) {
      const isEnvioRole = user?.rol?.nombre === 'Envíos' || user?.role === 'envios' || user?.rolId === 2;
      navigate(isEnvioRole ? '/envios-repartidor' : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleUsuarioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsuario(e.target.value);
    if (e.target.value.trim()) {
      setFieldErrors(prev => ({ ...prev, usuario: false }));
    }
    if (error && e.target.value.trim() && password.trim()) {
      setError('');
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (e.target.value.trim()) {
      setFieldErrors(prev => ({ ...prev, password: false }));
    }
    if (error && usuario.trim() && e.target.value.trim()) {
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setFieldErrors({ usuario: false, password: false });

    if (!usuario.trim() || !password.trim()) {
      setFieldErrors({
        usuario: !usuario.trim(),
        password: !password.trim(),
      });
      setError('Por favor, completa todos los campos.');
      return;
    }

    setIsSubmitting(true);
    setLoading(true);

    try {
      const response = await api.post('/auth/login', {
        usuario: usuario.trim(),
        contrasena: password.trim(),
      });

      if (response.data.success) {
        const userData = response.data.data.user;
        const token = response.data.data.token;

        login(userData, token);
        toast.success('Bienvenido al Gestor de Stock');

        const isEnvioRole = userData.rol?.nombre === 'Envíos' || userData.role === 'envios' || userData.rolId === 2;
        navigate(isEnvioRole ? '/envios-repartidor' : '/dashboard', { replace: true });
      }
    } catch (error: any) {
      const mensajeError = error.response?.data?.error || 'Error al iniciar sesión';

      if (mensajeError.toLowerCase().includes('inactivo')) {
        setError('Usuario inactivo.');
      } else if (mensajeError === 'Credenciales incorrectas') {
        setError('Credenciales incorrectas.');
        setFieldErrors({ usuario: true, password: true });
      } else {
        setError(mensajeError);
      }
      toast.error(mensajeError);
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
      {/* Panel izquierdo: Imagen*/}
      <div className="hidden lg:flex relative items-center justify-center overflow-hidden lg:order-1">
        {/* Imagen de fondo */}
        <img
          src="/imagenes-admin/hero_login_nuevo.webp"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Overlay con gradiente azul para legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900/95 via-primary-800/80 to-primary-900/70" />

        {/* Contenido sobre la imagen */}
        <div className="relative z-10 max-w-md px-10 text-center text-white">
          <h2 className="text-4xl font-bold tracking-tight drop-shadow-lg">
            ¡Bienvenido de nuevo!
          </h2>
          <p className="mt-4 text-base text-primary-100 leading-relaxed drop-shadow">
            Ingresá tus credenciales para acceder al panel de administración.
          </p>

          {/* Feature list */}
          <ul className="mt-8 space-y-3 text-left text-sm text-primary-50">
            {[
              'Gestión integral de productos y stock',
              'Control de ventas, compras y gastos',
              'Reportes y estadísticas en tiempo real',
            ].map((item) => (
              <li key={item} className="flex items-center gap-3">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-white/20 shrink-0 backdrop-blur-sm">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer del panel */}
        <div className="absolute bottom-6 left-0 right-0 text-center text-xs text-primary-200/80 z-10">
          Sistema interno · Acceso restringido
        </div>
      </div>

      {/* Panel derecho: Formulario*/}
      <div className="relative flex items-center justify-center px-6 py-10 sm:px-10 lg:px-16 bg-white lg:order-2 overflow-hidden">

        {/* Grid sutil */}
        <div
          className="absolute inset-0 opacity-[0.4] pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(to right, #e2e8f0 1px, transparent 1px),
              linear-gradient(to bottom, #e2e8f0 1px, transparent 1px)
            `,
            backgroundSize: '48px 48px',
          }}
          aria-hidden="true"
        />

        {/* Blob azul arriba a la derecha */}
        <div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-primary-200/40 blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        {/* Blob azul abajo a la izquierda */}
        <div
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-primary-100/50 blur-3xl pointer-events-none"
          aria-hidden="true"
        />

        {/* Contenido del formulario*/}
        <div className="relative z-10 w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img
              src="/imagenes-logos/logo_header1.webp"
              alt="Mi Tecno Hogar"
              className="h-14 w-auto object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/favicon.ico';
              }}
            />
          </div>

          {/* Título */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-slate-900">
              Mi <span className="text-primary-600">Tecno</span> Hogar
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Panel de Administración
            </p>
          </div>

          {/* Alerta de error general */}
          {error && (
            <div
              role="alert"
              className={`mb-5 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${
                error.includes('completa')
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}
            >
              <svg
                className="w-5 h-5 shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <Input
              id="username"
              label="Usuario"
              type="text"
              placeholder="Ingresá tu usuario"
              maxLength={50}
              value={usuario}
              onChange={handleUsuarioChange}
              onKeyDown={handleKeyPress}
              error={fieldErrors.usuario ? 'Ingresá tu usuario' : undefined}
              autoFocus
              autoComplete="username"
              disabled={isSubmitting}
            />

            <Input
              id="password"
              label="Contraseña"
              type="password"
              placeholder="Ingresá tu contraseña"
              maxLength={50}
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={handleKeyPress}
              error={fieldErrors.password ? 'Ingresá tu contraseña' : undefined}
              autoComplete="current-password"
              disabled={isSubmitting}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={isSubmitting}
              className="!bg-primary-600 hover:!bg-primary-700 focus:!ring-primary-500"
            >
              Iniciar Sesión
            </Button>
          </form>

          {/* Footer */}
          <p className="mt-8 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} Mi Tecno Hogar · Todos los derechos reservados.
          </p>
        </div>
      </div>

      {/* Botón flotante: Página Web*/}
      <a
        href="https://mitecnohogar.com.ar/productos"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-white text-primary-700 px-4 py-2.5 text-sm font-medium shadow-lg border border-primary-100 hover:bg-primary-50 hover:shadow-xl transition-all"
        aria-label="Ir a la página web"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 21a9 9 0 100-18 9 9 0 000 18zM3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18"
          />
        </svg>
        <span className="hidden sm:inline">Ver sitio web</span>
      </a>
    </div>
  );
}